import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';

const TrainingDetailsModal = ({ isOpen, training, onClose, actionMessage: initialActionMessage, fetchTrainings, onActionComplete }) => {
  
  const [actionMessage, setActionMessage] = useState(initialActionMessage || '');
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedTraining, setEditedTraining] = useState({});
  const [isAttendanceMode, setIsAttendanceMode] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [balanceData, setBalanceData] = useState(null);
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState('');
  const [editReason, setEditReason] = useState('');
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [savedBatches, setSavedBatches] = useState(new Set());
  //const [batchFiles, setBatchFiles] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);
  const [editedAmountSpent, setEditedAmountSpent] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionReason, setShowRejectionReason] = useState(false);

  // Memoized values
  const trainingCode = useMemo(() => {
    if (!training) return '';
    return `${training.institute}/${training.scheme}/${training.center}/${training.year}/${training.month}/${training.stitle}/${training.sno}`;
  }, [training]);
/*
  const isApproved = training?.status === 'Approved';
  const isRejected = training?.status === 'Rejected';
  const isDeleted = training?.status === 'Deleted';
  */

  //New chages start here 

 const isApproved = training?.status === 'Approved';
  const isRejected = training?.status === 'Rejected';
  const isDeleted = training?.status === 'Deleted';

  const treasuryDocumentPath = Array.isArray(training?.ppa_doc)
    ? training.ppa_doc[0]
    : training?.ppa_doc || '';

  const isDistrictTraining = training?.institute === 'DRC';

  const canManageTreasuryDocument =
    Boolean(userRole) &&
    isApproved &&
    !isDeleted &&
    !isEditMode &&
    (
      userRole !== 'department' ||
      (userRole === 'department' && isDistrictTraining)
    );

  //new chages end here 
  // Check if ALL batches have attendance data
  const allBatchesHaveAttendance = useMemo(() => {
    return editedTraining.batchesData?.every(batch => 
      batch.attendance && (batch.attendance.male > 0 || batch.attendance.female > 0)
    ) || false;
  }, [editedTraining.batchesData]);

  // ALL HOOKS MOVED TO TOP - BEFORE ANY CONDITIONAL LOGIC

  // Initialize data
  useEffect(() => {
    const role = localStorage.getItem('userRole');
    setUserRole(role);
  }, []);

  useEffect(() => {
    if (isOpen) {
      console.log('🔥 Modal opened with training:', training);
      console.log('🔥 Training ID:', training?.id);
      console.log('🔥 Training title:', training?.title);
    }
  }, [isOpen, training]);

  useEffect(() => {
    if (training) {
      setEditedTraining({
        amount: training.amount || '',
        batchesData: training.batchesData || []
      });
      setEditedAmountSpent(
        training.amount_spent ??
        training.training_amount_spent ??
        ''
      );
      
      const initialAttendance = {};
      const savedBatchesSet = new Set();
      
      training.batchesData?.forEach((batch) => {

        /*
        initialAttendance[batch.batchNo] = {
          male: batch.attendance?.male || 0,
          female: batch.attendance?.female || 0,
          amount_spent: batch.amount_spent || ''
        };  */
        initialAttendance[batch.batchNo] = {
          male: batch.attendance?.male || 0,
          female: batch.attendance?.female || 0
        };
        if (batch.attendance && (batch.attendance.male > 0 || batch.attendance.female > 0)) {
          savedBatchesSet.add(batch.batchNo);
        }
      });
      
      setAttendanceData(initialAttendance);
      setSavedBatches(savedBatchesSet);
    }
  }, [training]);

  // Utility functions
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().substr(-2);
    return `${day}/${month}/${year}`;
  }, []);

  const safeRender = useCallback((value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  }, []);

  const generateBatchCode = useCallback((trainingCode, batchNumber) => {
    const formattedBatchNumber = batchNumber.toString().padStart(2, '0');
    return `${trainingCode}/${formattedBatchNumber}`;
  }, []);

const getUploadUrl = useCallback((filePath) => {
  if (!filePath) return '';

  const cleanPath = String(filePath).trim();

  if (
    cleanPath.startsWith('http://') ||
    cleanPath.startsWith('https://')
  ) {
    return cleanPath;
  }

  const normalizedPath = cleanPath.replace(/^\/+/, '');

  if (normalizedPath.startsWith('uploads/')) {
    return `https://crd-training.in/${normalizedPath}`;
  }

  return `https://crd-training.in/uploads/${normalizedPath}`;
}, []);

const toAmount = useCallback((value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const cleanedValue = String(value).replace(/,/g, '').trim();
  const numericValue = parseFloat(cleanedValue);

  return Number.isNaN(numericValue) ? 0 : numericValue;
}, []);

const formatCurrency = useCallback((amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(toAmount(amount));
}, [toAmount]);

const getTrainingAmountSpent = useCallback(() => {
  return toAmount(editedAmountSpent);
}, [editedAmountSpent, toAmount]);

const getTrainingBalanceAmount = useCallback(() => {
  const amountAllocated = toAmount(training?.amount);
  const amountSpent = getTrainingAmountSpent();

  return amountAllocated - amountSpent;
}, [training?.amount, getTrainingAmountSpent, toAmount]);

  const getStatusColor = useCallback((status) => {
    switch(status) {
      case 'Approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'Rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'Deleted': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  }, []);

  const getActionMessageStyle = useCallback((message) => {
    if (message.includes('Error')) return 'bg-red-50 text-red-700 border border-red-100';
    if (message.includes('Approving') || message.includes('Rejecting') || message.includes('Saving') || message.includes('Deleting') || message.includes('Updating') || message.includes('Uploading')) {
      return 'bg-blue-50 text-blue-700 border border-blue-100';
    }
    return 'bg-green-50 text-green-700 border border-green-100';
  }, []);

  const showMessage = useCallback((msg, timeout = 3000) => {
    setActionMessage(msg);
    setTimeout(() => setActionMessage(''), timeout);
  }, []);

  // Event handlers
  const handleFieldChange = useCallback((field, value) => {
    setEditedTraining(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleBatchChange = useCallback((index, field, value) => {
    const updatedBatches = [...editedTraining.batchesData];
    updatedBatches[index] = { ...updatedBatches[index], [field]: value };
    setEditedTraining(prev => ({ ...prev, batchesData: updatedBatches }));
  }, [editedTraining.batchesData]);

  const handleAddBatch = useCallback(() => {
  setEditedTraining(prev => {
    const existingBatches = Array.isArray(prev.batchesData)
      ? prev.batchesData
      : [];

    const maxBatchNo = existingBatches.reduce((max, batch) => {
      const batchNo = parseInt(batch.batchNo, 10);
      return Number.isNaN(batchNo) ? max : Math.max(max, batchNo);
    }, 0);

    const nextBatchNo = String(maxBatchNo + 1).padStart(2, '0');

    return {
      ...prev,
      batchesData: [
        ...existingBatches,
        {
          batchNo: nextBatchNo,
          fromDate: '',
          toDate: '',
          participants: ''
        }
      ]
    };
  });
}, []);

const handleRemoveNewBatch = useCallback((index) => {
  const originalBatchCount = Array.isArray(training?.batchesData)
    ? training.batchesData.length
    : 0;

  if (index < originalBatchCount) {
    showMessage('Existing batch cannot be removed here', 3000);
    return;
  }

  setEditedTraining(prev => ({
    ...prev,
    batchesData: prev.batchesData.filter((_, batchIndex) => batchIndex !== index)
  }));
}, [training?.batchesData, showMessage]);

  const handleAttendanceChange = useCallback((batchNo, field, value) => {
    setAttendanceData(prev => ({
      ...prev,
      [batchNo]: { ...prev[batchNo], [field]: value }
    }));
  }, []);


  {/* 
  // File upload for specific batch
  const handleBatchPhotoChange = useCallback(async (event, batchNo) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      showMessage('Error: Please select a valid image file (JPEG, PNG, GIF)', 5000);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showMessage('Error: Photo file size must be less than 10MB', 5000);
      return;
    }
    
    try {
      setActionMessage(`Uploading photo for batch ${batchNo}...`);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('training_id', training.id);
      formData.append('type', 'photo');

      const response = await fetch(`${api.BASE_URL}/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': localStorage.getItem('authToken') },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setBatchFiles(prev => ({
          ...prev,
          [batchNo]: { ...prev[batchNo], photo: data.file }
        }));
        showMessage(`Photo uploaded for batch ${batchNo}!`);
      } else {
        const errorData = await response.json();
        showMessage('Error: ' + (errorData.message || 'Failed to upload photo'), 5000);
      }
    } catch (err) {
      showMessage('Error: ' + (err.message || 'Upload failed'), 5000);
    }
  }, [training?.id, showMessage]);
*/}

const handleTrainingPhotoUpload = useCallback(async (event) => {
  const files = Array.from(event.target.files || []);
  if (files.length === 0) return;

  const existingPhotos = Array.isArray(training?.training_photos)
    ? training.training_photos
    : training?.training_photos
      ? [training.training_photos]
      : [];

  if (existingPhotos.length >= 2) {
    showMessage('Error: Maximum 2 photos only allowed', 5000);
    event.target.value = '';
    return;
  }

  if (existingPhotos.length + files.length > 2) {
    showMessage('Error: You can upload maximum 2 photos only', 5000);
    event.target.value = '';
    return;
  }

  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];

  for (const file of files) {
    if (!allowedTypes.includes(file.type)) {
      showMessage('Error: Please select valid image files only', 5000);
      event.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showMessage('Error: Each photo file size must be less than 10MB', 5000);
      event.target.value = '';
      return;
    }
  }

  try {
    setPhotoUploading(true);
    setActionMessage('Uploading training photo...');

    const uploadedPhotos = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('training_id', training.id);
      formData.append('type', 'photo');

      const uploadResponse = await fetch(`${api.BASE_URL}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('authToken')
        },
        body: formData
      });

      const uploadData = await uploadResponse.json();

      if (!uploadResponse.ok || uploadData.error !== false || !uploadData.file) {
        throw new Error(uploadData.message || 'Photo upload failed');
      }

      uploadedPhotos.push(uploadData.file);
    }

    const updatedPhotos = [
      ...existingPhotos,
      ...uploadedPhotos
    ];

    const updateResponse = await fetch(`${api.BASE_URL}/project/training/${training.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify({
        training_photos: updatedPhotos
      })
    });

    const updateData = await updateResponse.json();

    if (!updateResponse.ok || updateData.error) {
      throw new Error(updateData.message || 'Failed to save photo path');
    }

    training.training_photos = updatedPhotos;
    setEditedTraining(prev => ({ ...prev }));

    showMessage('Training photo uploaded successfully', 3000);
    event.target.value = '';
  } catch (error) {
    console.error('Training photo upload error:', error);
    showMessage(`Error: ${error.message || 'Photo upload failed'}`, 5000);
  } finally {
    setPhotoUploading(false);
  }
}, [training, showMessage]);

const handleRemoveTrainingPhoto = useCallback(async (photoPath) => {
  try {
    const existingPhotos = Array.isArray(training?.training_photos)
      ? training.training_photos
      : training?.training_photos
        ? [training.training_photos]
        : [];

    const updatedPhotos = existingPhotos.filter(
      photo => photo !== photoPath
    );

    const updateResponse = await fetch(`${api.BASE_URL}/project/training/${training.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify({
        training_photos: updatedPhotos
      })
    });

    const updateData = await updateResponse.json();

    if (!updateResponse.ok || updateData.error) {
      throw new Error(updateData.message || 'Failed to remove photo');
    }

    training.training_photos = updatedPhotos;
    setEditedTraining(prev => ({ ...prev }));

    showMessage('Training photo removed successfully', 3000);
  } catch (error) {
    console.error('Remove photo error:', error);
    showMessage(`Error: ${error.message || 'Failed to remove photo'}`, 5000);
  }
}, [training, showMessage]);


const handleSaveAmountSpent = useCallback(async () => {
  try {
    setIsLoading(true);
    setActionMessage('Saving Amount Spent...');

    const response = await fetch(`${api.BASE_URL}/project/training/${training.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify({
        amount_spent: toAmount(editedAmountSpent)
      })
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(data.message || 'Failed to save Amount Spent');
    }

    training.amount_spent = toAmount(editedAmountSpent);
    setEditedAmountSpent(String(toAmount(editedAmountSpent)));

    if (typeof fetchTrainings === 'function') {
      fetchTrainings();
    }

    showMessage('Amount Spent saved successfully', 3000);
  } catch (error) {
    console.error('Save Amount Spent error:', error);
    showMessage(`Error: ${error.message || 'Failed to save Amount Spent'}`, 5000);
  } finally {
    setIsLoading(false);
  }
}, [training, editedAmountSpent, toAmount, fetchTrainings, showMessage]);

const handlePPAUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  if (file.type !== 'application/pdf') {
    showMessage('Error: Please select a PDF file', 5000);
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    showMessage('Error: PDF file size must be less than 10MB', 5000);
    return;
  }

  try {
    setActionMessage('Uploading Treasury Reference Number / Treasury Bill document...');
    setIsLoading(true);

    // Step 1: Upload the file
    const formData = new FormData();
    formData.append('file', file);
    formData.append('training_id', training.id);
    formData.append('type', 'ppa');

    const uploadResponse = await fetch(`${api.BASE_URL}/admin/upload`, {
      method: 'POST',
      headers: { 'Authorization': localStorage.getItem('authToken') },
      body: formData
    });

    const uploadData = await uploadResponse.json();
    
    // Check if upload was successful using error: false
    if (uploadData.error === false && uploadData.file) {
      const ppaFilePath = uploadData.file;

      setActionMessage('Saving PPA document path...');

      // Step 2: Update the training record with the PPA document path using POST
      const updateResponse = await fetch(`${api.BASE_URL}/project/training/${training.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify({
          ppa_doc: [ppaFilePath]
        })
      });

      const updateData = await updateResponse.json();

      if (updateResponse.ok && !updateData.error) {
        showMessage('Treasury Reference Number / Treasury Bill document uploaded and saved successfully', 3000);
        
        // Update the training object locally
        training.ppa_doc = [ppaFilePath];
        
        // Force a re-render to show the updated UI
        setEditedTraining(prev => ({ ...prev }));
        
        // ✅ Removed fetchTrainings() - No page refresh needed!
      } else {
        showMessage(`Error: ${updateData.message || 'Failed to save PPA document path'}`, 5000);
      }

    } else {
      // Upload failed
      showMessage(`Error: ${uploadData.message || 'Failed to upload PPA document'}`, 5000);
    }

  } catch (error) {
    console.error('Error uploading PPA document:', error);
    showMessage('Error: Failed to upload PPA document', 5000);
  } finally {
    setIsLoading(false);
  }
};
  const handleSubmitAttendance = useCallback(async (batchNo) => {
    try {
      setIsLoading(true);
      setActionMessage(`Updating attendance for batch ${batchNo}...`);
      
      const batchData = attendanceData[batchNo];
      const formattedBatchNo = batchNo.toString().padStart(2, '0');
      
      const requestData = {
        attendance: {
          male: parseInt(batchData.male) || 0,
          female: parseInt(batchData.female) || 0
        },
       // amount_spent: batchData.amount_spent.toString(),
       // batch_photo: batchFiles[batchNo]?.photo || ""
      };
      
      const response = await fetch(`${api.BASE_URL}/project/training/${training.id}/${formattedBatchNo}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(requestData)
      });
      
      if (response.ok) {
        setSavedBatches(prev => new Set([...prev, batchNo]));
        
        const updatedBatches = [...editedTraining.batchesData];
        const batchIndex = updatedBatches.findIndex(b => b.batchNo === batchNo);
        if (batchIndex !== -1) {
          updatedBatches[batchIndex] = {
            ...updatedBatches[batchIndex],
            attendance: {
              male: parseInt(batchData.male) || 0,
              female: parseInt(batchData.female) || 0
            },
           // amount_spent: batchData.amount_spent
          };
          setEditedTraining(prev => ({ ...prev, batchesData: updatedBatches }));
        }
        
        showMessage(`Attendance saved successfully for batch ${batchNo}!`);
      } else {
        const errorData = await response.json();
        showMessage('Error: ' + (errorData.message || 'Failed to update attendance'), 5000);
      }
    } catch (err) {
      showMessage('Error: ' + (err.message || 'Unknown error occurred'), 5000);
    } finally {
      setIsLoading(false);
    }
  }, [attendanceData, training?.id, editedTraining.batchesData, showMessage]);

 // }, [attendanceData, batchFiles, training?.id, editedTraining.batchesData, showMessage]);

  // Save changes
  const handleSave = useCallback(async () => {
    try {
      setIsLoading(true);
      setActionMessage('Saving changes...');
      
      const changedFields = {};
      const originalData = {
        amount: training.amount || '',
        batchesData: training.batchesData || []
      };

      if (editedTraining.amount !== originalData.amount) {
        changedFields.amount = editedTraining.amount;
      }

      const originalBatchesStr = JSON.stringify(originalData.batchesData.map(batch => ({
        fromDate: batch.fromDate,
        toDate: batch.toDate,
        participants: batch.participants
      })));
      
      const editedBatchesStr = JSON.stringify(editedTraining.batchesData.map(batch => ({
        fromDate: batch.fromDate,
        toDate: batch.toDate,
        participants: batch.participants
      })));

      if (originalBatchesStr !== editedBatchesStr) {
        changedFields.batchesData = editedTraining.batchesData;
      }

      if (Object.keys(changedFields).length === 0) {
        showMessage('No changes to save', 1500);
        setTimeout(() => {
          setIsEditMode(false);
          setShowReasonInput(false);
        }, 1500);
        return;
      }

      if (userRole !== 'department' && editReason.trim()) {
        changedFields.editReason = editReason.trim();
      }

      const response = await fetch(`${api.BASE_URL}/project/training/${training.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(changedFields)
      });
      
      if (response.ok) {
        showMessage('Changes saved successfully!');
        setIsEditMode(false);
        setShowReasonInput(false);
        setEditReason('');
        if (typeof fetchTrainings === 'function') fetchTrainings();
        setTimeout(onClose, 1500);
      } else {
        const errorData = await response.json();
        showMessage('Error: ' + (errorData.message || 'Failed to save changes'), 5000);
      }
    } catch (err) {
      showMessage('Error: ' + (err.message || 'Unknown error occurred'), 5000);
    } finally {
      setIsLoading(false);
    }
  }, [editedTraining, training, userRole, editReason, fetchTrainings, onClose, showMessage]);

  // Balance check
  const checkBalance = useCallback(async (centerName, scheme, amount) => {
    try {
      setIsCheckingBalance(true);
      setBalanceError('');
      
      const balanceEndpoint = `${centerName}-${scheme}`;
      const response = await fetch(`${api.BASE_URL}/ledger/balance/${balanceEndpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': localStorage.getItem('authToken'),
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const balanceData = await response.json();
        setBalanceData(balanceData);
        
        const availableBalance = balanceData.data?.[0] ? parseFloat(balanceData.data[0].balance) : 0;
        const requestedAmount = parseFloat(amount) || 0;
        
        if (requestedAmount > availableBalance) {
          setBalanceError(`Insufficient balance! Available: ₹${availableBalance}, Requested: ₹${requestedAmount}`);
          return false;
        }
        return true;
      } else {
        const errorData = await response.json();
        setBalanceError('Error checking balance: ' + (errorData.message || 'Failed to fetch balance'));
        return false;
      }
    } catch (err) {
      setBalanceError('Error checking balance: ' + (err.message || 'Network error occurred'));
      return false;
    } finally {
      setIsCheckingBalance(false);
    }
  }, []);

const handleApprove = useCallback(async () => {
    if (isApproved) return;
    
    try {
      setIsLoading(true);
      setActionMessage('Checking balance...');
      
      const centerName = training.center;
      const scheme = training.scheme;
      const amount = editedTraining.amount || training.amount;
      
      if (!centerName || !scheme || !amount) {
        showMessage('Error: Missing required data for approval', 5000);
        return;
      }
      
      const canApprove = await checkBalance(centerName, scheme, amount);
      
      if (!canApprove) {
        showMessage(balanceError || 'Cannot approve due to insufficient balance', 8000);
        return;
      }
      
      setActionMessage('Approving training...');
      const response = await api.training.approveTraining(training.id);
      
      if (response && !response.error) {
        showMessage('Training approved successfully!');
        window.dispatchEvent(new CustomEvent('refreshNotifications'));

        if (typeof onActionComplete === 'function') {
          await onActionComplete(training, 'Approved');
        } else {
          if (typeof fetchTrainings === 'function') {
            await fetchTrainings();
          }
          setTimeout(onClose, 1500);
        }
      } else {
        showMessage('Error: ' + (response.message || 'Failed to approve training'), 5000);
      }
    } catch (err) {
      showMessage('Error: ' + (err.message || 'Unknown error occurred'), 5000);
    } finally {
      setIsLoading(false);
    }
  }, [isApproved, training, editedTraining, checkBalance, balanceError, fetchTrainings, onClose, onActionComplete, showMessage]);

  // Reject training
  const handleReject = useCallback(async () => {
    if (!rejectionReason.trim()) {
      showMessage('Please provide a reason for rejection', 3000);
      return;
    }
    
    try {
      setIsLoading(true);
      setActionMessage('Rejecting training...');
      
      const response = await fetch(`${api.BASE_URL}/project/training/${training.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify({ 
          status: 'Rejected',
          rejectionReason: rejectionReason.trim()
        })
      });
      
      if (response.ok) {
        showMessage('Training rejected successfully!');
        setShowRejectionReason(false);
        setRejectionReason('');
        if (typeof onActionComplete === 'function') {
          await onActionComplete(training, 'Rejected');
        } else {
          if (typeof fetchTrainings === 'function') {
            await fetchTrainings();
          }
          setTimeout(onClose, 1500);
        }
      } else {
        const errorData = await response.json();
        showMessage('Error: ' + (errorData.message || 'Failed to reject training'), 5000);
      }
    } catch (err) {
      showMessage('Error: ' + (err.message || 'Unknown error occurred'), 5000);
    } finally {
      setIsLoading(false);
    }
  }, [rejectionReason, training, fetchTrainings, onClose, onActionComplete, showMessage]);

  // Delete training
  const handleDelete = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete this training? This action cannot be undone.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      setActionMessage('Deleting training...');
      
      const response = await fetch(`${api.BASE_URL}/project/training/${training.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify({ status: 'Deleted' })
      });
      
      if (response.ok) {
        showMessage('Training deleted successfully!');
        if (typeof fetchTrainings === 'function') fetchTrainings();
        setTimeout(onClose, 1500);
      } else {
        const errorData = await response.json();
        showMessage('Error: ' + (errorData.message || 'Failed to delete training'), 5000);
      }
    } catch (err) {
      showMessage('Error: ' + (err.message || 'Unknown error occurred'), 5000);
    } finally {
      setIsLoading(false);
      setTimeout(onClose, 2000);
    }
  }, [training?.id, fetchTrainings, onClose, showMessage]);

  // EARLY RETURNS AFTER ALL HOOKS
  if (!isOpen) return null;
  
  if (!training || !training.id) {
    console.error('🔥 No valid training data!');
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg">
          <h3 className="text-red-600 mb-4">Error: No training data</h3>
          <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center">
            <h3 className="text-lg font-semibold text-gray-900">Training Details</h3>
            <div className={`ml-3 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(training.status || 'Pending')}`}>
              {training.status || 'Pending'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
            disabled={isLoading}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Action Message */}
        {actionMessage && (
          <div className={`px-6 py-3 ${getActionMessageStyle(actionMessage)} rounded-md mx-6 my-3 text-center text-sm font-medium`}>
            {actionMessage}
          </div>
        )}

        {/* Rejection Reason Section for department users */}
        {userRole === 'department' && showRejectionReason && (
          <div className="mx-6 my-3">
            <div className="bg-red-50 rounded-lg border border-red-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-red-100 border-b border-red-200">
                <h4 className="font-medium text-red-800">Reason for Rejection</h4>
              </div>
              <div className="p-4">
                <label className="block text-sm font-medium text-red-700 mb-2">
                  Please provide a reason for rejecting this training:
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full rounded-md border-red-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                  rows="3"
                  placeholder="Enter reason for rejection..."
                  required
                />
                <div className="mt-3 flex gap-3">
                  <button
                    onClick={handleReject}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    disabled={!rejectionReason.trim() || isLoading}
                  >
                    {isLoading ? 'Rejecting...' : 'Confirm Rejection'}
                  </button>
                  <button
                    onClick={() => {
                      setShowRejectionReason(false);
                      setRejectionReason('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Training Code */}
            <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm break-all border border-gray-200 shadow-sm">
              <div className="text-xs text-gray-500 mb-1 font-sans">Training Code</div>
              {trainingCode}
            </div>

            {/* Training Information */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Training Information</h4>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Title</h4>
                    <p className="mt-1 text-gray-900">{safeRender(training.title || training.training)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Type</h4>
                    <p className="mt-1 text-gray-900">{safeRender(training.type)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Institute</h4>
                    <p className="mt-1 text-gray-900">{safeRender(training.institute)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Scheme</h4>
                    <p className="mt-1 text-gray-900">{safeRender(training.scheme_name)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">A.S Date</h4>
                    <p className="mt-1 text-gray-900">
                      {training.asDate ? new Date(training.asDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">A.S Number</h4>
                    <p className="mt-1 text-gray-900">{safeRender(training.asNumber)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Amount</h4>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={editedTraining.amount}
                        onChange={(e) => handleFieldChange('amount', e.target.value)}
                        className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{safeRender(training.amount)}</p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Center</h4>
                    <p className="mt-1 text-gray-900">{safeRender(training.center_name)}</p>
                  </div>
                  
                  {/* A.S File - Only for department users */}
                  {userRole === 'department' && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">A.S File</h4>
                      {training.asFile && training.asFile.trim() !== '' ? (
                        <a 
                          href={`https://crd-training.in/uploads/${training.asFile}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="mt-1 text-blue-600 hover:text-blue-800 underline flex items-center"
                        >
                          <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                           A.S File
                        </a>
                      ) : (
                        <p className="mt-1 text-gray-900">No file uploaded</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

{/* Training Photos - Max 2 photos, not batch-wise */}
<div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
    <h4 className="font-medium text-gray-700">Training Photos</h4>
  </div>

  <div className="p-4">
    {(() => {
      const trainingPhotos = Array.isArray(training?.training_photos)
        ? training.training_photos
        : training?.training_photos
          ? [training.training_photos]
          : [];

      return (
        <>
          {trainingPhotos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {trainingPhotos.map((photoPath, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                >
                  <a
                    href={getUploadUrl(photoPath)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                  >
                    View Photo {index + 1}
                  </a>

                  {userRole !== 'department' && !isDeleted && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTrainingPhoto(photoPath)}
                      className="ml-3 text-red-600 hover:text-red-800 text-sm"
                      disabled={photoUploading || isLoading}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 mb-4">
              No photos uploaded
            </p>
          )}

          {userRole !== 'department' && !isDeleted && trainingPhotos.length < 2 && (
            <div>
              <label className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 cursor-pointer">
                {photoUploading ? 'Uploading...' : 'Upload Training Photo'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  multiple
                  onChange={handleTrainingPhotoUpload}
                  disabled={photoUploading || isLoading}
                  className="hidden"
                />
              </label>

              <p className="mt-2 text-xs text-gray-500">
                Maximum 2 photos only. Batch-wise photo upload is not required.
              </p>
            </div>
          )}

          {trainingPhotos.length >= 2 && (
            <p className="text-xs text-green-600">
              Maximum 2 photos uploaded.
            </p>
          )}
        </>
      );
    })()}
  </div>
</div>


{/* PPA Document - Show for all users */}
<div>
  <h4 className="text-sm font-medium text-gray-500">Treasury Reference Number / Treasury Bill Document</h4>
  {training.ppa_doc && training.ppa_doc.length > 0 && training.ppa_doc[0] ? (
    <a 
      href={`https://crd-training.in/uploads/${training.ppa_doc[0]}`} 
      target="_blank" 
      rel="noopener noreferrer"
      className="mt-1 text-blue-600 hover:text-blue-800 underline flex items-center"
    >
      <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      {/*View PPA Document */}
      View Treasury Reference Number / Treasury Bill Document
    </a>
  ) : (
    <p className="mt-1 text-gray-900">No document uploaded</p>
  )}
</div>
            {/* Balance Information */}
            {balanceData && (
              <div className="bg-blue-50 rounded-lg border border-blue-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-blue-100 border-b border-blue-200">
                  <h4 className="font-medium text-blue-800">Balance Information</h4>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-blue-600">Available Balance</h4>
                      <p className="mt-1 text-blue-900 font-semibold">₹{balanceData.data?.[0]?.balance || 0}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-blue-600">Requested Amount</h4>
                      <p className="mt-1 text-blue-900 font-semibold">₹{editedTraining.amount || training.amount}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

{/* Clientele Information - Only show if not Module or PLC */}
{!training.isModule && !training.isPLC && (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 className="font-medium text-gray-700">Clientele Information</h4>
    </div>
    <div className="p-4">
      {Array.isArray(training.clientile) && training.clientile.length > 0 ? (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {training.clientile.map((client, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {client.typeLabel || client.type || 'N/A'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {client.member || 'N/A'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                    {client.nos || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 italic">No clientele information available</p>
      )}
    </div>
  </div>
)}

{/* Module Information - Only for SIR institute when isModule is true */}
{training.institute === 'SIR' && training.isModule && (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 className="font-medium text-gray-700">Module Information</h4>
    </div>
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-500">Number of Modules</h4>
          <p className="mt-1 text-gray-900">{training.numberOfModules || 'N/A'}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500">Module Amount</h4>
          <p className="mt-1 text-gray-900">{training.moduleAmount ? `₹${training.moduleAmount}` : 'N/A'}</p>
        </div>
{training.moduleFile && training.moduleFile.trim() !== '' && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Module File</h4>
            <a 
              href={`https://crd-training.in/uploads/${training.moduleFile}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center text-blue-600 hover:text-blue-800 underline"
            >
              <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View Module File
            </a>
          </div>
        )}
        {training.moduleRemarks && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-500">Remarks</h4>
            <p className="mt-1 text-gray-900 whitespace-pre-wrap">{training.moduleRemarks}</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}

{/* PLC Information - Only for SIR institute when isPLC is true */}
{training.institute === 'SIR' && training.isPLC && (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 className="font-medium text-gray-700">PLC Information</h4>
    </div>
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-500">Number of PLC</h4>
          <p className="mt-1 text-gray-900">{training.numberOfPLC || 'N/A'}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500">PLC Amount</h4>
          <p className="mt-1 text-gray-900">{training.plcAmount ? `₹${training.plcAmount}` : 'N/A'}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500">Remarks</h4>
          <p className="mt-1 text-gray-900">{training.remarks || 'N/A'}</p>
        </div>
{training.signedCopyFile && training.signedCopyFile.trim() !== '' && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Signed Copy File</h4>
            <a 
              href={`https://crd-training.in/uploads/${training.signedCopyFile}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center text-blue-600 hover:text-blue-800 underline"
            >
              <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View Signed Copy
            </a>
          </div>
        )}
        {training.remarks && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-500">Remarks</h4>
            <p className="mt-1 text-gray-900 whitespace-pre-wrap">{training.remarks}</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}
{/* IEC Information - Only for SIR institute when isIEC is true */}
{training.institute === 'SIR' && training.isIEC && (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 className="font-medium text-gray-700">IEC Information</h4>
    </div>
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-500">IEC Amount</h4>
          <p className="mt-1 text-gray-900">{training.iecAmount ? `₹${training.iecAmount}` : 'N/A'}</p>
        </div>
        <div className="md:col-span-2">
          <h4 className="text-sm font-medium text-gray-500">IEC Remarks</h4>
          <p className="mt-1 text-gray-900 whitespace-pre-wrap">{training.iecRemarks || 'N/A'}</p>
        </div>
      </div>
    </div>
  </div>
)}
{/* Handholding Information - Only for SIR institute when isHandholding is true */}
{training.institute === 'SIR' && training.isHandholding && (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 className="font-medium text-gray-700">Handholding Support Information</h4>
    </div>
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-500">Number of Districts</h4>
          <p className="mt-1 text-gray-900">{training.numberOfDistricts || 'N/A'}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500">Number of Institutions</h4>
          <p className="mt-1 text-gray-900">{training.numberOfInstitutions || 'N/A'}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500">Handholding Amount</h4>
          <p className="mt-1 text-gray-900">{training.handholdingAmount ? `₹${training.handholdingAmount}` : 'N/A'}</p>
        </div>
        <div className="md:col-span-2">
          <h4 className="text-sm font-medium text-gray-500">Remarks</h4>
          <p className="mt-1 text-gray-900 whitespace-pre-wrap">{training.handholdingRemarks || 'N/A'}</p>
        </div>
{training.handholdingFile && training.handholdingFile.trim() !== '' && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Handholding File</h4>
            <a 
              href={`https://crd-training.in/uploads/${training.handholdingFile}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center text-blue-600 hover:text-blue-800 underline"
            >
              <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View Handholding File
            </a>
          </div>
        )}
        {training.handholdingRemarks && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-500">Remarks</h4>
            <p className="mt-1 text-gray-900 whitespace-pre-wrap">{training.handholdingRemarks}</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}{/* PMU Information - Only for SIR institute when isPMU is true */}
{training.institute === 'SIR' && training.isPMU && (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 className="font-medium text-gray-700">PMU Information</h4>
    </div>
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-500">PMU Type</h4>
          <p className="mt-1 text-gray-900">{training.pmuType || 'N/A'}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500">Amount</h4>
          <p className="mt-1 text-gray-900">{training.amount ? `₹${training.amount}` : 'N/A'}</p>
        </div>
{training.pmuFile && training.pmuFile.trim() !== '' && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-500 mb-2">PMU File</h4>
            <a 
              href={`https://crd-training.in/uploads/${training.pmuFile}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center text-blue-600 hover:text-blue-800 underline"
            >
              <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View PMU File
            </a>
          </div>
        )}
        {training.pmuRemarks && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-500">Remarks</h4>
            <p className="mt-1 text-gray-900 whitespace-pre-wrap">{training.pmuRemarks}</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}
            {/* Batch Information */}
            {editedTraining.batchesData && editedTraining.batchesData.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-medium text-gray-700">Batch Information</h4>
                </div>
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Code</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Participants</th>

                          {isEditMode && (
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Action
                            </th>
                          )}
                          {userRole !== 'department' && isAttendanceMode && (
                            <>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Male</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Female</th>
                            {/* <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Spent (₹)</th> */} 
                            {/*  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Photo</th>*/} 
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {editedTraining.batchesData.map((batch, index) => {
                          const isBatchSaved = savedBatches.has(batch.batchNo);
                          const hasAttendance = batch.attendance && (batch.attendance.male > 0 || batch.attendance.female > 0);
                          
                          return (
                            <tr key={index} className={`transition-colors ${isBatchSaved ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                              <td className="px-4 py-3 text-sm font-mono text-gray-700">
                                {generateBatchCode(trainingCode, parseInt(batch.batchNo))}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                {isEditMode ? (
                                  <div className="flex gap-2">
                                    <input
                                      type="date"
                                      value={batch.fromDate ? batch.fromDate.split('T')[0] : ''}
                                      onChange={(e) => handleBatchChange(index, 'fromDate', e.target.value)}
                                      className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    />
                                    <span className="self-center">to</span>
                                    <input
                                      type="date"
                                      value={batch.toDate ? batch.toDate.split('T')[0] : ''}
                                      onChange={(e) => handleBatchChange(index, 'toDate', e.target.value)}
                                      className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    />
                                  </div>
                                ) : (
                                  `${formatDate(batch.fromDate)} to ${formatDate(batch.toDate)}`
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                                {isEditMode ? (
                                  <input
                                    type="text"
                                    value={typeof batch.participants === 'object' 
                                      ? JSON.stringify(batch.participants) 
                                      : batch.participants}
                                    onChange={(e) => handleBatchChange(index, 'participants', e.target.value)}
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-right"
                                  />
                                ) : (
                                  <div>
                                    <div>{typeof batch.participants === 'object' 
                                      ? JSON.stringify(batch.participants) 
                                      : batch.participants}</div>
                                    {hasAttendance && (
                                      <div className="text-xs text-gray-600 mt-1 space-y-1">
                                        <div>Male: {batch.attendance.male || 0}</div>
                                        <div>Female: {batch.attendance.female || 0}</div>
                                        <div>Total: {(batch.attendance.male || 0) + (batch.attendance.female || 0)}</div>
                                        {/*{batch.amount_spent && <div>Spent: ₹{batch.amount_spent}</div>} */}
                                      </div>
                                    )}
                                    {/*  Batch-wise photo view removed as per requirement 
                                    
                                    {batch.batch_photo && (
                                      <button
                                        onClick={() => window.open(`https://crd-training.in/uploads/${batch.batch_photo}`, '_blank')}
                                        className="px-2 py-1 mt-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                      >
                                        View Photo
                                      </button>
                                    )}
                                      */}
                                  </div>
                                )}
                              </td>

                              {isEditMode && (
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                  {index >= (training?.batchesData?.length || 0) ? (
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveNewBatch(index)}
                                      className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
                                      disabled={isLoading}
                                    >
                                      Remove
                                    </button>
                                  ) : (
                                    <span className="text-xs text-gray-400">Existing</span>
                                  )}
                                </td>
                              )}

                              {userRole !== 'department' && isAttendanceMode && (
                                <>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                    <input
                                      type="number"
                                      value={hasAttendance ? batch.attendance.male : (attendanceData[batch.batchNo]?.male || '')}
                                      onChange={(e) => handleAttendanceChange(batch.batchNo, 'male', e.target.value)}
                                      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-center"
                                      min="0"
                                      placeholder="0"
                                      disabled={isBatchSaved}
                                    />
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                    <input
                                      type="number"
                                      value={hasAttendance ? batch.attendance.female : (attendanceData[batch.batchNo]?.female || '')}
                                      onChange={(e) => handleAttendanceChange(batch.batchNo, 'female', e.target.value)}
                                      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-center"
                                      min="0"
                                      placeholder="0"
                                      disabled={isBatchSaved}
                                    />
                                  </td>

                                  {/*
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                    <input
                                      type="text"
                                      value={hasAttendance ? batch.amount_spent : (attendanceData[batch.batchNo]?.amount_spent || '')}
                                      onChange={(e) => handleAttendanceChange(batch.batchNo, 'amount_spent', e.target.value)}
                                      className="w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-center"
                                      placeholder="Enter Amount"
                                      disabled={isBatchSaved}
                                    />
                                  </td>
                                  */}

                                  {/*}
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                    {!isBatchSaved ? (
                                      <div className="space-y-2">
                                        <input
                                          type="file"
                                          accept="image/*"
                                          onChange={(e) => handleBatchPhotoChange(e, batch.batchNo)}
                                          className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700"
                                          disabled={isLoading}
                                        />
                                        {batchFiles[batch.batchNo]?.photo && (
                                          <span className="text-xs text-green-600">✓ Uploaded</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-green-600">✓ Photo Saved</span>
                                    )}
                                  </td>
                                  */}
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                    {!isBatchSaved ? (
                                      <button
                                        onClick={() => handleSubmitAttendance(batch.batchNo)}
                                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                        disabled={isLoading}
                                      >
                                        Save
                                      </button>
                                    ) : (
                                      <span className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-md">
                                        ✓ Saved
                                      </span>
                                    )}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {/* Financial Summary */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Financial Summary</h4>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <h4 className="text-sm font-medium text-blue-700">
                      Amount Allocated
                    </h4>
                    <p className="mt-1 text-blue-900 font-semibold">
                      {formatCurrency(training.amount)}
                    </p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                    <h4 className="text-sm font-medium text-orange-700">
                      Amount Spent
                    </h4>

                    <input
                      type="number"
                      min="0"
                      value={editedAmountSpent}
                      onChange={(event) => setEditedAmountSpent(event.target.value)}
                      className="mt-2 w-full rounded-md border-orange-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      placeholder="Enter Amount Spent"
                      disabled={userRole === 'department' || !isApproved || isDeleted || isLoading}
                    />

                    <p className="mt-2 text-orange-900 font-semibold">
                      {formatCurrency(getTrainingAmountSpent())}
                    </p>

                    {userRole !== 'department' && isApproved && !isDeleted && (
                      <button
                        type="button"
                        onClick={handleSaveAmountSpent}
                        className="mt-3 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Saving...' : 'Save Amount Spent'}
                      </button>
                    )}
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <h4 className="text-sm font-medium text-green-700">
                      Balance Amount
                    </h4>
                    <p className="mt-1 text-green-900 font-semibold">
                      {formatCurrency(getTrainingBalanceAmount())}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Edit Reason Section for non-department users */}
            {userRole !== 'department' && (showReasonInput || isEditMode) && !isDeleted && (
              <div className="bg-orange-50 rounded-lg border border-orange-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-orange-100 border-b border-orange-200">
                  <h4 className="font-medium text-orange-800">Reason for Edit Dates / Add Batches</h4>
                </div>
                <div className="p-4">
                  <label className="block text-sm font-medium text-orange-700 mb-2">
                    Please provide a reason for editing dates or adding batches:
                  </label>
                  <textarea
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    className="w-full rounded-md border-orange-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    rows="3"
                    placeholder="Enter reason for editing dates or adding batches."
                    required
                  />

                {isEditMode && (
                  <div className="mt-3 mb-3">
                    <button
                      type="button"
                      onClick={handleAddBatch}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                      disabled={isLoading}
                    >
                      + Add Batch
                    </button>

                    <p className="mt-2 text-xs text-orange-700">
                      New batches can be added from this Edit Dates / Add Batches section.
                    </p>
                  </div>
                )}

                  {showReasonInput && !isEditMode && (
                    <div className="mt-3 flex gap-3">
                      <button
                        onClick={() => {
                          if (editReason.trim()) {
                            setShowReasonInput(false);
                            setIsEditMode(true);
                          } else {
                            showMessage('Please provide a reason for editing', 3000);
                          }
                        }}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                      >
                        Continue Edit
                      </button>
                      <button
                        onClick={() => {
                          setShowReasonInput(false);
                          setEditReason('');
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
            disabled={isLoading}
          >
            Close
          </button>
          
          {/* Update Attendance button for non-department users */}
          {userRole !== 'department' && !isDeleted && !isEditMode && isApproved && !allBatchesHaveAttendance && (
            <button
              onClick={() => setIsAttendanceMode(!isAttendanceMode)}
              className={`px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${
                isAttendanceMode 
                  ? 'bg-purple-700 hover:bg-purple-800' 
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
              disabled={isLoading}
            >
              {isAttendanceMode ? 'Hide Attendance Form' : 'Update Attendance'}
            </button>
          )}
        
       {/* Treasury Reference Number / Treasury Bill */}
        {!isDeleted && !isEditMode && isDistrictTraining && (
          <>
            {treasuryDocumentPath ? (
              <button
                type="button"
                onClick={() =>
                  window.open(
                    `https://crd-training.in/uploads/${treasuryDocumentPath}`,
                    '_blank'
                  )
                }
                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                View Treasury Reference Number / Treasury Bill Document
              </button>
            ) : (
              userRole !== 'department' &&
              isApproved && (
                <label className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors cursor-pointer">
                  {isLoading
                    ? 'Uploading...'
                    : 'Upload Treasury Reference Number / Treasury Bill Document'}

                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handlePPAUpload}
                    className="hidden"
                    disabled={isLoading}
                  />
                </label>
              )
            )}
          </>
        )}

          {/* Edit and Save buttons for department users */}
          {userRole === 'department' && !isDeleted && (
            <>
              {!isEditMode ? (
                <button
                  onClick={() => setIsEditMode(true)}
                  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  disabled={isLoading}
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsEditMode(false);
                      setEditReason('');
                      setShowReasonInput(false);
                      setEditedTraining({
                        amount: training.amount || '',
                        batchesData: training.batchesData || []
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm transition-colors"
                    disabled={isLoading}
                  >
                    Cancel Edit
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
            </>
          )}

{/* Edit button for non-department users (dates only) - Only show if batchesData exists */}
{userRole !== 'department' && !isDeleted && !isAttendanceMode && editedTraining.batchesData && editedTraining.batchesData.length > 0 && (
  <>
    {!isEditMode && !showReasonInput ? (
      <button
        onClick={() => setShowReasonInput(true)}
        className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
        disabled={isLoading}
      >
        Edit Dates / Add Batches
      </button>
    ) : isEditMode ? (
      <>
        <button
          onClick={() => {
            setIsEditMode(false);
            setShowReasonInput(false);
            setEditReason('');
            setEditedTraining({
              amount: training.amount || '',
              batchesData: training.batchesData || []
            });
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm transition-colors"
          disabled={isLoading}
        >
          Cancel Edit
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </>
    ) : null}
  </>
)}
          
          {/* Delete button - visible for department users when not in edit mode, not already deleted, and not approved */}
          {userRole === 'department' && !isEditMode && !isDeleted && isApproved && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm transition-colors"
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </button>
          )}
          
          {/* Approve/Reject buttons only visible when not in edit mode and not deleted */}
          {userRole === 'department' && !isApproved && !isRejected && !isDeleted && !isEditMode && (
            <>
              <button
                onClick={() => setShowRejectionReason(true)}
                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                disabled={isLoading}
              >
                Reject
              </button>
              <button
                onClick={handleApprove}
                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                disabled={isLoading || isCheckingBalance}
              >
                {isLoading || isCheckingBalance ? (isCheckingBalance ? 'Checking Balance...' : 'Processing...') : 'Approve'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingDetailsModal;